import {
  slate,
  Tool,
  MigrateToV2,
  type ToolArgsWithController,
} from '@aha-app/aha-editor-v2';
import AhaAnalytics from 'javascripts/analytics';
import { Note, NoteTemplate } from 'components/framework/models';
import * as Sentry from '@sentry/browser';

import { chooseTemplate } from '../components/PageTemplateModal';
import {
  createWhiteboardRecord,
  insertEmbeddedWhiteboard,
  embeddedWhiteboardPlaceholder,
} from 'components/aha_editor_field_v2/WhiteboardUtils';
import { type InsertTemplateConfiguration } from 'components/aha_editor_field_v2/controllers/AhaEditorFieldV2Controller';
import type ApplicationController from '@aha-app/mvc';
import {
  AHA_DOCUMENT_TEMPLATE_PLUGIN_ID,
  type AhaDocumentTemplatePlugin,
} from '../AhaDocumentTemplatePlugin';

const { Node, Editor, Text, Range } = slate;

type TemplateController = ApplicationController & {
  insertTemplateConfig: InsertTemplateConfiguration;
};

function findClosestTemplateController(
  editorController: ApplicationController
): TemplateController | undefined {
  return editorController.findController(
    controller =>
      'insertTemplateConfig' in controller &&
      Boolean(controller.insertTemplateConfig)
  ) as TemplateController | undefined;
}

export default class ToolInsertTemplate extends Tool {
  static toolName = 'Template';

  static iconClass: string = 'fa-regular fa-table-layout';

  static available({ controller }: ToolArgsWithController): boolean {
    return Boolean(findClosestTemplateController(controller));
  }

  static action(toolActionArgs: ToolArgsWithController) {
    const { editor, controller: editorController } = toolActionArgs;
    const controller = findClosestTemplateController(editorController);

    if (!controller) return;

    const {
      projectId,
      showWhiteboardTemplates,
      baseURL = '',
      getSharingToken,
      objectId,
      objectType,
    } = controller.insertTemplateConfig;
    void chooseTemplate({ showWhiteboardTemplates, projectId }).then(
      payload => {
        if (!payload) return;

        const { template, isGlobal } = payload;
        if (template.whiteboard && objectId) {
          const placeholder = embeddedWhiteboardPlaceholder();

          slate.Editor.insertNode(editor, placeholder);

          void createWhiteboardRecord({
            objectType,
            objectId,
            baseURL,
            getSharingToken,
            templateId: template.id,
            isGlobalTemplate: isGlobal,
          }).then(res => {
            const {
              whiteboard: { id: whiteboardId },
            } = res;
            const placeholderId = placeholder.placeholder_id;
            insertEmbeddedWhiteboard({ editor, whiteboardId, placeholderId });
          });
        } else {
          if (objectId && isGlobal && template.id) {
            Note.linkToTemplate(objectId, template.id).catch(err => {
              console.error('Error linking template', err);
              Sentry.captureException(err);
            });
          }

          let content;
          if (template instanceof NoteTemplate) {
            content = template.editorContent;
          } else if (template.description) {
            content = template.description.editorContent;
          }

          if (!content) return;
          const fragment = MigrateToV2(content);
          const children = JSON.parse(
            JSON.stringify(fragment.children)
          ) as (typeof fragment)['children'];
          editor.insertFragment(children);
        }

        const documentTemplatePlugin: AhaDocumentTemplatePlugin | undefined =
          editorController.ahaEditor.getPlugin(AHA_DOCUMENT_TEMPLATE_PLUGIN_ID);

        documentTemplatePlugin?.api.onAddTemplate(template, isGlobal);

        AhaAnalytics.track(
          'Feature-7094629539429452113',
          'User applies a template',
          {
            Location: 'Text editor',
            'Template name': template.name,
          }
        );
      }
    );
  }

  static isEnabled(toolArgs: ToolArgsWithController) {
    // These are the conditions on which inserting a template is allowed:
    // - The selection must exist and be collapsed.
    // - The full ancestor tree of the selected node must consist only of
    //   The editor itself, paragraphs, headings or text nodes.

    const { editor, controller: editorController } = toolArgs;
    const controller = findClosestTemplateController(editorController);
    if (!controller) return false;

    // If there is a selection, we need to ensure it's valid
    if (editor.selection) {
      if (Range.isExpanded(editor.selection)) {
        return false;
      }

      const {
        anchor: { path },
      } = editor.selection;

      const ancestorsGenerator = Node.ancestors(editor, path, {
        reverse: true,
      });
      const allowedNodeTypes = [
        'paragraph',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
      ];
      for (const [ancestor] of ancestorsGenerator) {
        if (Text.isText(ancestor)) continue;
        if (Editor.isEditor(ancestor)) continue;

        if (!allowedNodeTypes.includes(ancestor.type)) return false;
      }
    }

    return true;
  }
}
